import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken } from '../api';
import { formatConstitution } from '../lib/constitutionLabels';

export default function AboutMe() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [aiText, setAiText] = useState('');
  const [loadingAi, setLoadingAi] = useState(true);

  useEffect(() => {
    if (!getToken()) { navigate('/login'); return; }
    api.get('/profile').then(setProfile).catch(() => {});
  }, [navigate]);

  useEffect(() => {
    api.get('/profile/read')
      .then(data => { setAiText(data.text); setLoadingAi(false); })
      .catch(() => setLoadingAi(false));
  }, []);

  if (!profile) {
    return (
      <div style={{ height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#000', paddingBottom: 100 }}>

      {/* Photo hero */}
      <div style={{ position: 'relative', height: '55vh', overflow: 'hidden', background: 'linear-gradient(160deg,#0a0a18,#141420)' }}>
        {profile.photo_url && (
          <img
            src={profile.photo_url}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.55) 70%, #000 100%)',
        }} />
        <div style={{ position: 'absolute', bottom: 28, left: 24, right: 24 }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 30, fontWeight: 300, color: '#fff', lineHeight: 1.2 }}>
            {profile.name}{profile.age ? `, ${profile.age}` : ''}
          </div>
          {profile.location && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', marginTop: 4, fontWeight: 300 }}>
              {profile.location}
            </div>
          )}
        </div>
      </div>

      {/* Trait pills */}
      {(profile.dosha_type || profile.astro_sign) && (
        <div style={{ display: 'flex', gap: 10, padding: '20px 24px 4px', flexWrap: 'wrap' }}>
          {profile.dosha_type && (
            <span style={{
              fontSize: 11, letterSpacing: 1.5, fontWeight: 400,
              padding: '5px 14px',
              color: 'var(--gold)',
              border: '0.5px solid rgba(201,168,76,0.4)',
            }}>
              {formatConstitution(profile.dosha_type)}
            </span>
          )}
          {profile.astro_sign && (
            <span style={{
              fontSize: 10, letterSpacing: 2, textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)', border: '0.5px solid rgba(255,255,255,0.12)',
              padding: '5px 12px', fontWeight: 400,
            }}>
              {profile.astro_sign}
            </span>
          )}
        </div>
      )}

      {/* AI insight */}
      <div style={{ padding: '28px 24px 0' }}>
        <div className="label" style={{ marginBottom: 16, color: 'var(--gold)', opacity: 0.7 }}>
          What we know about you
        </div>
        {loadingAi ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--muted)', fontSize: 13 }}>
            <span className="spinner" style={{ width: 14, height: 14 }} />
            Reading your profile...
          </div>
        ) : aiText ? (
          <>
            <div style={{ marginBottom: 18 }}>
              {aiText.split('\n').map(l => l.replace(/^•\s*/, '').trim()).filter(Boolean).map((line, i, arr) => (
                <div key={i}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '10px 0' }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)', marginTop: 7, flexShrink: 0 }} />
                    <span style={{
                      fontFamily: 'var(--font-display)',
                      fontStyle: 'italic',
                      fontSize: 16,
                      fontWeight: 300,
                      lineHeight: 1.6,
                      color: 'rgba(255,255,255,0.85)',
                    }}>{line}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <div style={{ height: '0.5px', background: 'rgba(255,255,255,0.08)' }} />
                  )}
                </div>
              ))}
            </div>
            <p style={{
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 12,
              fontWeight: 300,
              color: 'var(--gold)',
              opacity: 0.7,
            }}>
              — Paula
            </p>
          </>
        ) : (
          <p style={{ fontSize: 13, color: 'var(--very-muted)', fontStyle: 'italic' }}>
            Complete your profile to see your insight.
          </p>
        )}
      </div>

      {/* Written bio */}
      {profile.bio && (
        <div style={{ padding: '32px 24px 0' }}>
          <div style={{ height: '0.5px', background: 'var(--border)', marginBottom: 28 }} />
          <div className="label" style={{ marginBottom: 16 }}>In my own words</div>
          <p className="body-text" style={{ color: 'rgba(255,255,255,0.65)' }}>{profile.bio}</p>
        </div>
      )}

      {/* Edit link */}
      <div style={{ padding: '36px 24px 0', textAlign: 'center' }}>
        <button
          className="link-btn"
          style={{ fontSize: 11, letterSpacing: 2, textTransform: 'uppercase' }}
          onClick={() => navigate('/settings')}
        >
          Edit profile →
        </button>
      </div>
    </div>
  );
}
